// apps/pwa-pos/src/lib/db-migration.ts
// Sprint 99 POS.5 — traslado one-shot de localStorage a IndexedDB.
//
// Todo se escribe en UNA transacción. Si el equipo se apaga a mitad —el
// escenario que motiva POS.5— la transacción no commitea, localStorage queda
// intacto y el próximo arranque reintenta. Las claves viejas se borran sólo
// después de que la transacción commiteó y de que cada raw leído sigue igual,
// con revalidación individual justo antes de cada borrado para reducir la
// ventana entre la verificación y el removeItem.
//
// Un registro inválido NO aborta la migración (C2 del review final de POS.5).
// El aborto era determinístico, así que se repetía en cada arranque para
// siempre, y el store menos valioso —historial— podía vetar la migración del
// más valioso —la cola de ventas—. Ahora cada store se resuelve por separado:
// los registros que no validan quedan en cuarentena (su clave de localStorage
// no se borra) y el resto migra igual. El problema se devuelve para que el
// arranque lo publique en la UI.

import { catalogoKey, getDb } from './db';
import { sembrarConsecutivo } from './consecutivo';
import type {
  CatalogoSnapshot,
  PendingAnular,
  PendingCajaOp,
  PendingVenta,
  VentaHistorialItem,
} from './queue-types';

export const MIGRATED_KEY = 'mibodega.idb.migrated';

const VENTAS_KEY = 'mibodega.sync-queue.ventas';
const CAJA_KEY = 'mibodega.sync-queue.caja';
const ANULAR_KEY = 'mibodega.sync-queue.anular';
const HISTORIAL_KEY = 'mibodega.historial.ventas';
const CATALOG_PREFIX = 'mibodega.catalog.';

const ESTADOS_VENTA = new Set(['pendiente', 'sincronizada', 'error', 'rechazada']);
const ESTADOS_CAJA = new Set(['pendiente', 'sincronizada', 'error', 'rechazada']);
const ESTADOS_ANULAR = new Set(['pendiente', 'sincronizada', 'error', 'rechazada']);
const ESTADOS_HISTORIAL = new Set(['sincronizada', 'anulada', 'error']);

export interface MigracionResultado {
  migrado: boolean;
  ventas: number;
  caja: number;
  anular: number;
  historial: number;
  catalogos: number;
  /** Mensajes de lo que quedó en cuarentena, para publicarlos en la UI. */
  problemas: string[];
}

class MigracionAbortada extends Error {
  constructor(mensaje: string) {
    super(mensaje);
    this.name = 'MigracionAbortada';
  }
}

function esStringNoVacio(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

function esNumeroFinito(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function esCartLineValido(l: unknown): boolean {
  if (!l || typeof l !== 'object') return false;
  const r = l as Record<string, unknown>;
  return (
    esStringNoVacio(r.productoId) &&
    esStringNoVacio(r.productoNombre) &&
    esStringNoVacio(r.presentacionId) &&
    esStringNoVacio(r.presentacionNombre) &&
    esNumeroFinito(r.cantidad) &&
    esNumeroFinito(r.precioUnitario) &&
    esNumeroFinito(r.subtotal)
  );
}

function esLineaDesgloseValida(l: unknown): boolean {
  if (!l || typeof l !== 'object') return false;
  const r = l as Record<string, unknown>;
  return esStringNoVacio(r.moneda) && esNumeroFinito(r.monto);
}

function esCatalogItemValido(i: unknown): boolean {
  if (!i || typeof i !== 'object') return false;
  const r = i as Record<string, unknown>;
  const category = r.category;
  const categoryValid =
    category === undefined ||
    category === null ||
    (typeof category === 'object' &&
      category !== null &&
      esStringNoVacio((category as Record<string, unknown>).id) &&
      esStringNoVacio((category as Record<string, unknown>).name) &&
      (((category as Record<string, unknown>).parentId === null &&
        (category as Record<string, unknown>).parentName === null) ||
        (esStringNoVacio((category as Record<string, unknown>).parentId) &&
          esStringNoVacio((category as Record<string, unknown>).parentName))));
  return (
    esStringNoVacio(r.id) &&
    esStringNoVacio(r.nombre) &&
    esStringNoVacio(r.presentacion_id) &&
    esStringNoVacio(r.presentacion_nombre) &&
    esNumeroFinito(r.precio) &&
    categoryValid
  );
}

function esVentaValida(v: unknown): v is PendingVenta {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  if (!(
    esStringNoVacio(r.id) &&
    esStringNoVacio(r.organizationId) &&
    esStringNoVacio(r.sucursalId) &&
    typeof r.fecha === 'string' &&
    typeof r.monedaCodigo === 'string' &&
    esNumeroFinito(r.total) &&
    typeof r.estado === 'string' &&
    ESTADOS_VENTA.has(r.estado) &&
    typeof r.createdAt === 'string' &&
    (esNumeroFinito(r.localConsecutivo) || esNumeroFinito(r.consecutivo))
  )) {
    return false;
  }
  if (!Array.isArray(r.lineas)) return false;
  for (const l of r.lineas) {
    if (!esCartLineValido(l)) return false;
  }
  return true;
}

function esCajaValida(o: unknown): o is PendingCajaOp {
  if (!o || typeof o !== 'object') return false;
  const r = o as Record<string, unknown>;
  if (!(
    esStringNoVacio(r.id) &&
    esStringNoVacio(r.sesionLocalId) &&
    esStringNoVacio(r.organizationId) &&
    esStringNoVacio(r.sucursalId) &&
    esStringNoVacio(r.usuarioId) &&
    typeof r.monedaBase === 'string' &&
    (r.tipo === 'APERTURA' || r.tipo === 'CIERRE') &&
    esStringNoVacio(r.idempotencyKey) &&
    typeof r.createdAt === 'string' &&
    typeof r.estado === 'string' &&
    ESTADOS_CAJA.has(r.estado)
  )) {
    return false;
  }
  if (!Array.isArray(r.desglose)) return false;
  for (const l of r.desglose) {
    if (!esLineaDesgloseValida(l)) return false;
  }
  return true;
}

function esAnularValida(o: unknown): o is PendingAnular {
  if (!o || typeof o !== 'object') return false;
  const r = o as Record<string, unknown>;
  return (
    esStringNoVacio(r.id) &&
    esStringNoVacio(r.organizationId) &&
    esStringNoVacio(r.ventaId) &&
    typeof r.motivo === 'string' &&
    typeof r.createdAt === 'string' &&
    typeof r.estado === 'string' &&
    ESTADOS_ANULAR.has(r.estado)
  );
}

function esHistorialValido(h: unknown): h is VentaHistorialItem {
  if (!h || typeof h !== 'object') return false;
  const r = h as Record<string, unknown>;
  return (
    esStringNoVacio(r.id) &&
    esStringNoVacio(r.organizationId) &&
    esStringNoVacio(r.sucursalId) &&
    typeof r.codigo === 'string' &&
    esNumeroFinito(r.total) &&
    typeof r.monedaCodigo === 'string' &&
    typeof r.fecha === 'string' &&
    typeof r.estado === 'string' &&
    ESTADOS_HISTORIAL.has(r.estado) &&
    typeof r.syncedAt === 'string'
  );
}

interface ColaParseada<T> {
  /** Registros que pasaron la validación y se van a migrar. */
  validos: T[];
  /** Todo lo que había en el array, valide o no. Sirve para sembrar el piso
   *  del consecutivo sin perder los números de un registro en cuarentena. */
  crudos: unknown[];
  /** Mensaje para la UI, o null si no hubo nada raro. */
  problema: string | null;
  /** true cuando algo quedó en cuarentena: la clave no se borra. */
  conservarClave: boolean;
}

/**
 * Parsea una cola legacy sin abortar la migración de las demás.
 *
 * Un problema a nivel de la clave —JSON roto, o algo que no es un array— deja
 * ese store en cero y conserva la clave. Un registro individual inválido se
 * saltea y el resto de esa misma cola migra igual; la clave se conserva para
 * que el dato en cuarentena siga en disco y sea recuperable a mano.
 */
function parsearCola<T>(
  raw: string | null | undefined,
  key: string,
  validador: (x: unknown) => x is T,
): ColaParseada<T> {
  const vacio: ColaParseada<T> = { validos: [], crudos: [], problema: null, conservarClave: false };
  if (raw === null || raw === undefined) return vacio;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {
      validos: [],
      crudos: [],
      problema: `${key}: JSON inválido. Los datos quedan en cuarentena en este equipo.`,
      conservarClave: true,
    };
  }
  if (!Array.isArray(parsed)) {
    return {
      validos: [],
      crudos: [],
      problema: `${key}: se esperaba un array. Los datos quedan en cuarentena en este equipo.`,
      conservarClave: true,
    };
  }

  const validos: T[] = [];
  const indicesInvalidos: number[] = [];
  for (let i = 0; i < parsed.length; i++) {
    if (validador(parsed[i])) validos.push(parsed[i] as T);
    else indicesInvalidos.push(i);
  }

  if (indicesInvalidos.length === 0) {
    return { validos, crudos: parsed, problema: null, conservarClave: false };
  }
  return {
    validos,
    crudos: parsed,
    problema:
      `${key}: ${indicesInvalidos.length} de ${parsed.length} registro(s) con forma inválida ` +
      `(índices ${indicesInvalidos.join(', ')}). Quedan en cuarentena en este equipo; ` +
      `los demás se migraron.`,
    conservarClave: true,
  };
}

/** Mayor consecutivo presente en las ventas legacy, valide o no el registro. */
function pisoDesdeVentas(crudos: unknown[]): number {
  let piso = 0;
  for (const v of crudos) {
    if (!v || typeof v !== 'object') continue;
    const r = v as Record<string, unknown>;
    for (const candidato of [r.localConsecutivo, r.consecutivo]) {
      const n = Number(candidato);
      if (Number.isFinite(n) && n > piso) piso = Math.floor(n);
    }
  }
  return piso;
}

/**
 * Mayor sufijo numérico de `historial.codigo`. Los códigos vienen de cinco
 * sprints de formatos distintos, así que se parsea a la defensiva y se descarta
 * todo lo que no sea un entero positivo (`V-LEGACY`, vacíos, basura).
 */
function pisoDesdeHistorial(crudos: unknown[]): number {
  let piso = 0;
  for (const h of crudos) {
    if (!h || typeof h !== 'object') continue;
    const codigo = (h as Record<string, unknown>).codigo;
    if (typeof codigo !== 'string') continue;
    const sufijo = codigo.split('-').pop()?.trim() ?? '';
    if (!/^\d+$/.test(sufijo)) continue;
    const n = Number.parseInt(sufijo, 10);
    if (Number.isFinite(n) && n > piso) piso = n;
  }
  return piso;
}

function normalizarVenta(raw: PendingVenta): PendingVenta {
  const r = { ...raw } as unknown as Record<string, unknown>;
  const consecutivoLegacy = r.consecutivo;
  delete r.consecutivo;
  r.localConsecutivo = esNumeroFinito(r.localConsecutivo)
    ? r.localConsecutivo
    : esNumeroFinito(consecutivoLegacy)
      ? consecutivoLegacy
      : 0;
  r.intentos = esNumeroFinito(r.intentos) ? r.intentos : 0;
  r.proximoIntentoAt = typeof r.proximoIntentoAt === 'string' ? r.proximoIntentoAt : null;
  return r as unknown as PendingVenta;
}

function normalizarCaja(raw: PendingCajaOp): PendingCajaOp {
  const r = raw as unknown as Record<string, unknown>;
  return {
    ...raw,
    intentos: esNumeroFinito(r.intentos) ? r.intentos : 0,
    proximoIntentoAt: typeof r.proximoIntentoAt === 'string' ? r.proximoIntentoAt : null,
    estado: r.estado as PendingCajaOp['estado'],
  };
}

function normalizarAnular(raw: PendingAnular): PendingAnular {
  const r = raw as unknown as Record<string, unknown>;
  return {
    ...raw,
    intentos: esNumeroFinito(r.intentos) ? r.intentos : 0,
    proximoIntentoAt: typeof r.proximoIntentoAt === 'string' ? r.proximoIntentoAt : null,
    estado: r.estado as PendingAnular['estado'],
  };
}

function normalizarHistorial(raw: VentaHistorialItem): VentaHistorialItem {
  return raw;
}

interface CatalogoBueno {
  key: string;
  snapshot: CatalogoSnapshot;
}

function parsearCatalogos(
  catalogKeys: Iterable<string>,
  rawsOriginales: Map<string, string | null>,
): { buenos: CatalogoBueno[]; malos: string[] } {
  const buenos: CatalogoBueno[] = [];
  const malos: string[] = [];
  for (const k of catalogKeys) {
    const raw = rawsOriginales.get(k);
    if (raw === null || raw === undefined) continue;
    let snap: unknown;
    try {
      snap = JSON.parse(raw);
    } catch {
      malos.push(k);
      continue;
    }
    if (!snap || typeof snap !== 'object' || !Array.isArray((snap as { items?: unknown }).items)) {
      malos.push(k);
      continue;
    }
    const items = (snap as { items: unknown[] }).items;
    let itemsValidos = true;
    for (const item of items) {
      if (!esCatalogItemValido(item)) {
        itemsValidos = false;
        break;
      }
    }
    if (!itemsValidos) {
      malos.push(k);
      continue;
    }
    const s = snap as Record<string, unknown>;
    const orgId = typeof s.orgId === 'string' ? s.orgId : null;
    const sucursalId = typeof s.sucursalId === 'string' ? s.sucursalId : null;
    const [orgKey, sucursalKey] = catalogoKey(orgId, sucursalId);
    buenos.push({
      key: k,
      snapshot: {
        orgKey,
        sucursalKey,
        items: items as CatalogoSnapshot['items'],
        refreshedAt: typeof s.refreshedAt === 'string' ? s.refreshedAt : new Date(0).toISOString(),
        orgId,
        sucursalId,
      },
    });
  }
  return { buenos, malos };
}

function setsIguales<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function clavesDeCatalogoActuales(): Set<string> {
  const keys = new Set<string>();
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(CATALOG_PREFIX)) keys.add(k);
  }
  return keys;
}

export async function migrarDesdeLocalStorage(): Promise<MigracionResultado> {
  const vacio: MigracionResultado = {
    migrado: false,
    ventas: 0,
    caja: 0,
    anular: 0,
    historial: 0,
    catalogos: 0,
    problemas: [],
  };

  if (typeof localStorage === 'undefined') return vacio;
  if (localStorage.getItem(MIGRATED_KEY) === '1') return vacio;

  // Phase 1: snapshot único de cada raw relevante. A partir de aquí, parsear
  // y normalizar operan SIEMPRE sobre los raws capturados — nunca más
  // localStorage.getItem para parsear, eso sería la carrera ABA.
  const rawsOriginales = new Map<string, string | null>();
  for (const k of [VENTAS_KEY, CAJA_KEY, ANULAR_KEY, HISTORIAL_KEY]) {
    rawsOriginales.set(k, localStorage.getItem(k));
  }
  const catalogKeysIniciales = clavesDeCatalogoActuales();
  for (const k of catalogKeysIniciales) {
    rawsOriginales.set(k, localStorage.getItem(k));
  }

  // Phase 2: parsear y normalizar desde el snapshot. Cada cola se resuelve por
  // separado: un registro roto en historial no puede vetar las ventas.
  const ventasParsed = parsearCola(rawsOriginales.get(VENTAS_KEY), VENTAS_KEY, esVentaValida);
  const cajaParsed = parsearCola(rawsOriginales.get(CAJA_KEY), CAJA_KEY, esCajaValida);
  const anularParsed = parsearCola(rawsOriginales.get(ANULAR_KEY), ANULAR_KEY, esAnularValida);
  const historialParsed = parsearCola(
    rawsOriginales.get(HISTORIAL_KEY),
    HISTORIAL_KEY,
    esHistorialValido,
  );

  const ventas = ventasParsed.validos.map(normalizarVenta);
  const caja = cajaParsed.validos.map(normalizarCaja);
  const anular = anularParsed.validos.map(normalizarAnular);
  const historial = historialParsed.validos.map(normalizarHistorial);

  const { buenos: catalogosBuenos, malos: catalogKeysMalos } = parsearCatalogos(
    catalogKeysIniciales,
    rawsOriginales,
  );

  const problemas = [
    ventasParsed.problema,
    cajaParsed.problema,
    anularParsed.problema,
    historialParsed.problema,
  ].filter((p): p is string => p !== null);

  // Claves que NO se borran: las que dejaron algo en cuarentena.
  const clavesConservadas = new Set<string>(catalogKeysMalos);
  if (ventasParsed.conservarClave) clavesConservadas.add(VENTAS_KEY);
  if (cajaParsed.conservarClave) clavesConservadas.add(CAJA_KEY);
  if (anularParsed.conservarClave) clavesConservadas.add(ANULAR_KEY);
  if (historialParsed.conservarClave) clavesConservadas.add(HISTORIAL_KEY);

  // Phase 3: escritura atómica en una sola tx readwrite con awaits serializados.
  const db = await getDb();
  const tx = db.transaction(
    ['ventas_queue', 'caja_queue', 'anular_queue', 'historial', 'catalogo'],
    'readwrite',
  );

  try {
    for (const v of ventas) {
      await tx.objectStore('ventas_queue').put(v);
    }
    for (const o of caja) {
      await tx.objectStore('caja_queue').put(o);
    }
    for (const o of anular) {
      await tx.objectStore('anular_queue').put(o);
    }
    for (const h of historial) {
      await tx.objectStore('historial').put(h);
    }
    for (const c of catalogosBuenos) {
      await tx.objectStore('catalogo').put(c.snapshot);
    }

    await tx.done;
  } catch (err) {
    // La tx ya abortó por sí sola al fallar un put; tx.done queda rechazada.
    // Suprimimos ese rechazo para que no aparezca como unhandled y re-lanzamos
    // el error original.
    void tx.done.catch(() => {});
    try {
      tx.abort();
    } catch {
      // La tx ya estaba en estado terminal; abort es no-op.
    }
    throw err;
  }

  // Phase 4: verificación final tras tx.done y antes de cualquier borrado.
  // 4a: el conjunto de claves de catálogo no cambió.
  const catalogKeysActuales = clavesDeCatalogoActuales();
  if (!setsIguales(catalogKeysIniciales, catalogKeysActuales)) {
    throw new MigracionAbortada(
      `Catálogo: conjunto de claves cambió durante la migración, se conservan todas las claves`,
    );
  }
  // 4b: cada raw que SÍ se va a borrar sigue igual al snapshot. Las claves en
  // cuarentena no se verifican porque no se borran.
  for (const [k, rawOriginal] of rawsOriginales) {
    if (clavesConservadas.has(k)) continue;
    if (localStorage.getItem(k) !== rawOriginal) {
      throw new MigracionAbortada(`${k}: cambió post-commit, no se borra nada`);
    }
  }

  // Phase 5: borrado y flag, con revalidación individual justo antes de cada
  // removeItem. Comparar-y-borrar no es atómico en localStorage, pero esta
  // revalidación reduce la ventana entre la verificación final y el borrado.
  const keysABorrar = [
    VENTAS_KEY,
    CAJA_KEY,
    ANULAR_KEY,
    HISTORIAL_KEY,
    ...catalogosBuenos.map((c) => c.key),
  ].filter((k) => !clavesConservadas.has(k));
  for (const k of keysABorrar) {
    if (localStorage.getItem(k) !== rawsOriginales.get(k)) {
      throw new MigracionAbortada(`${k}: cambió justo antes del borrado, no se borra nada`);
    }
    localStorage.removeItem(k);
  }
  localStorage.setItem(MIGRATED_KEY, '1');

  // I2 — sembrar el piso del consecutivo local. Sin esto, un equipo que se
  // actualiza con la cola vacía —el estado normal de un local con conectividad—
  // arranca el contador en 0 y la próxima venta reimprime V-YYYY-000001,
  // duplicando códigos que ya están en historial y en tickets impresos.
  sembrarConsecutivo(
    Math.max(pisoDesdeVentas(ventasParsed.crudos), pisoDesdeHistorial(historialParsed.crudos)),
  );

  return {
    migrado: true,
    ventas: ventas.length,
    caja: caja.length,
    anular: anular.length,
    historial: historial.length,
    catalogos: catalogosBuenos.length,
    problemas,
  };
}
